// WeekView.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  GetIndexExactlyMatchingColumn,
  IsDateBetweenTwoDates,
} from "../Shared/Utility";

/**
 * TODO: Replace this stub with your actual DefaultTS implementation or import.
 * It must implement:
 *  - GetIndexExactlyMatchingColumn(array, columnName, value) => index
 *  - IsDateBetweenTwoDates(startDate, targetDate, endDate) => boolean
 */
const defaultTSStub = {
  GetIndexExactlyMatchingColumn: (arr: any[], col: string, val: any) => {
    if (!arr) return -1;
    for (let i = 0; i < arr.length; i++) {
      // handles string/number comparisons similar to original
      // NOTE: adjust as required
      if (arr[i] && arr[i][col] === val) return i;
    }
    return -1;
  },
  IsDateBetweenTwoDates: (start: Date, target: Date, end: Date) => {
    const s =
      start instanceof Date ? start.getTime() : new Date(start).getTime();
    const t =
      target instanceof Date ? target.getTime() : new Date(target).getTime();
    const e = end instanceof Date ? end.getTime() : new Date(end).getTime();
    return t >= s && t <= e;
  },
};

type DateObj = { year: number; month: number; date: number };

type WeekViewProps = {
  m_oSelectedDate: any;
  m_oCurrentPage: DateObj;
  m_oToday: DateObj;
  m_arrEvents?: any[];
  Events?: any[];
  ToDoList?: any[];
  EventsList?: any[];
  AirportTransfers?: any[];
  ResourcesBookingsList?: any[];
  TotalResourcesGridData?: any[];
  TotalAvailableResourcesGridData?: any[];
  ResourceBookingGridData?: any[];
  EventRequirementGridData?: any[];
  EventNameDetails?: any;
  AMTRunningOrder?: any[];
  AMTJsonDataForEvents?: any[];
  WeekendDays?: any[];
  PublicHolidays?: any[];
  Helpers?: any[];
  IsValueChanged?: boolean;
  Show24Hours?: boolean;
  BankHolidayText?: string;
  // event emitter replacement
  dayClicked?: (payload: any) => void;
};

const WeekView: React.FC<WeekViewProps> = (props) => {
  // Short accessors for props with defaults like Angular inputs
  const {
    m_oSelectedDate,
    m_oCurrentPage,
    m_oToday,
    EventsList = [],
    Helpers = [],
    Show24Hours = false,
    PublicHolidays = [],
    AMTJsonDataForEvents = [],
    BankHolidayText = "",
    dayClicked,
  } = props;

  // Refs
  const tablescrollRef = useRef<HTMLDivElement | null>(null);
  const wrapper1Ref = useRef<HTMLDivElement | null>(null);
  const wrapper2Ref = useRef<HTMLDivElement | null>(null);

  // State variables mirroring Angular component
  const [IsEventsDataBinded, setIsEventsDataBinded] = useState(false);
  const [m_arrHours, setMArrHours] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [m_arrDayDates, setMArrDayDates] = useState<any[]>([]);
  const [m_arrEvents, setMArrEvents] = useState<any[]>([]);
  const [CalculatedDataFromDates, setCalculatedDataFromDates] = useState<any[]>(
    []
  );
  const [AvailableHeight, setAvailableHeight] = useState<number>(
    window.innerHeight
  );
  const [TableWidth, setTableWidth] = useState<number>(10);
  const [IsBinded, setIsBinded] = useState(false);
  const [PerBoxWidth, setPerBoxWidth] = useState<number>(0);
  const [TotalWidth, setTotalWidth] = useState<number>(0);
  const [l_arrWeekDates, setLArrWeekDates] = useState<any[]>([]);

  const Default = defaultTSStub; // TODO: replace with real DefaultTS import

  // mouse drag to scroll
  const isDraggingRef = useRef(false);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);

  // day names
  const m_arrDayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // local copy of AMTJsonDataForEvents so changes in props are reflected
  const [AMTJsonDataForEventsState, setAMTJsonDataForEventsState] = useState<
    any[]
  >(AMTJsonDataForEvents || []);

  // keep some values in refs for stable DOM manipulations
  const CalculatedDataFromDatesRef = useRef<any[]>([]);
  const m_arrHoursRef = useRef<any[]>([]);
  const usersRef = useRef<any[]>([]);
  const PerBoxWidthRef = useRef<number>(0);

  // ---------- Utility / Lifecycle equivalents ----------

  // GenerateHours
  const GenerateHours = useCallback(() => {
    let l_intStartTime: any = 0;
    const arr: any[] = [];
    for (let i = 0; i < 24; i++) {
      if ((i > 7 && i < 20) || Show24Hours) {
        if (i < 12) {
          if (i < 10)
            arr.push({
              Hour: "0" + l_intStartTime + " : 00",
              Text: "",
              id: l_intStartTime,
              StartMinutes: 0,
              Duration: 0,
              StartTime: 0,
              EndTime: 0,
            });
          else
            arr.push({
              Hour: l_intStartTime + " : 00",
              Text: "",
              id: l_intStartTime,
              StartMinutes: 0,
              Duration: 0,
              StartTime: 0,
              EndTime: 0,
            });
        } else
          arr.push({
            Hour: l_intStartTime + " : 00",
            Text: "",
            id: l_intStartTime,
            StartMinutes: 0,
            Duration: 0,
            StartTime: 0,
            EndTime: 0,
          });
      }
      l_intStartTime = parseInt(l_intStartTime) + 1;
    }
    setMArrHours(arr);
    m_arrHoursRef.current = arr;
  }, [Show24Hours]);

  // ChangeWeek (p_intAddWeek)
  const ChangeWeek = useCallback(
    (p_intAddWeek: number) => {
      // NOTE: We expect m_oCurrentPage to be passed as a mutable-like object similar to Angular
      // Make a local copy
      const l_oDate = new Date(
        m_oCurrentPage.year,
        m_oCurrentPage.month,
        m_oCurrentPage.date
      );
      l_oDate.setDate(l_oDate.getDate() + p_intAddWeek);

      const newPage = {
        date: l_oDate.getDate(),
        month: l_oDate.getMonth(),
        year: l_oDate.getFullYear(),
      };
      // If parent expects to mutate m_oCurrentPage, they should pass updated props — but we keep local day dates calculation
      // Compute week start (Sunday)
      const l_intWeekDay = l_oDate.getDay();
      l_oDate.setDate(l_oDate.getDate() - l_intWeekDay);

      const newDayDates: any[] = [];
      for (let i = 0; i < 7; i++) {
        const l_strDate = l_oDate.toString();
        newDayDates[i] = { DateName: l_strDate, Date: l_oDate.getDate() };
        l_oDate.setDate(l_oDate.getDate() + 1);
      }
      setMArrDayDates(newDayDates);
      setIsEventsDataBinded(false);
      // Generate Dates For Week
      GenerateDatesForWeek(newDayDates, newPage);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [m_oCurrentPage, GenerateHours]
  );

  // GenerateDatesForWeek (depends on m_arrDayDates and m_oCurrentPage)
  const GenerateDatesForWeek = useCallback(
    (dayDates?: any[], pageOverride?: any) => {
      const l_arrCalendarData = dayDates || m_arrDayDates;
      const l_intCount = 0;
      const weekDates: any[] = [];
      for (let i = 0; i < l_arrCalendarData.length; i++) {
        const d = new Date(
          (pageOverride || m_oCurrentPage).year,
          (pageOverride || m_oCurrentPage).month,
          l_arrCalendarData[i].Date
        );
        weekDates.push(d);
      }
      setLArrWeekDates(weekDates);
      // Clear previously bound events
      setMArrEvents([]);
      RemovePreviouslyBindedEventsBoxesFromJson_Called();
      setCalculatedDataFromDates([]);
      // Check dates for events
      CheckDatesForEventFromJson(EventsList);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [m_arrDayDates, m_oCurrentPage, EventsList]
  );

  // CheckDatesForEventFromJson -- big function ported from Angular
  const CheckDatesForEventFromJson = useCallback(
    (p_arrGridData: any[]) => {
      let Index = -1;
      const l_arrEvents: any[] = []; // for calculated events used in rendering
      const l_arrEventsForJson: any[] = [];
      let l_intCount = 0;
      if (l_arrEventsForJson.length > 0) l_intCount = l_arrEventsForJson.length;

      const AMT = p_arrGridData || AMTJsonDataForEventsState || [];
      // store in state
      setAMTJsonDataForEventsState(AMT);

      // we'll fill l_arrEvents and l_arrEventsForJson
      for (let i = 0; i < m_arrHoursRef.current.length; i++) {
        let l_boolMatched = false;
        let l_strText = "";
        let l_strTooltipText = "";
        let l_intStartMinutesJson = 0;
        let l_intDurationMinutes = 0;
        let l_strStartTime = "";
        let l_strEndTime = "";
        let l_intCounter = 0;

        // current page date (m_oToday)
        const l_oCurrentPageDate = m_oToday;
        const l_intCurrentYear = l_oCurrentPageDate.year;
        const l_intCurrentMonth = l_oCurrentPageDate.month;
        const l_intCurrentDay = l_oCurrentPageDate.date;

        for (let j = 0; j < AMT.length; j++) {
          if (AMT[j].StartTime != " ") {
            const l_strStartDateInDB = AMT[j].StartTime;
            let l_strEndDateInDB = AMT[j].EndTime;
            let l_boolIsPersonalLeave = false;
            if (AMT[j].SerialNumber == "3") l_boolIsPersonalLeave = true;
            let l_strURL = "events";
            if (AMT[j].URL) l_strURL = AMT[j].URL;

            let l_strColor = "#A3E1D4";
            if (AMT[j].Color) l_strColor = AMT[j].Color;

            let l_boolAllDay: any = false;
            if (AMT[j].AllDay) l_boolAllDay = AMT[j].AllDay;

            const l_oDate: any = new Date(l_strStartDateInDB);
            if (l_oDate.toString() != "Invalid Date") {
              const l_intDay = l_oDate.getDate();
              const l_intMonth = l_oDate.getMonth();
              const l_intYear = l_oDate.getFullYear();
              let l_intStartHour = l_oDate.getHours();
              let l_intStartMinutes = l_oDate.getMinutes();

              l_strEndDateInDB = new Date(l_strEndDateInDB) as any;
              const l_intEndDay = l_strEndDateInDB.getDate();
              const l_intEndMonth = l_strEndDateInDB.getMonth();
              const l_intEndYear = l_strEndDateInDB.getFullYear();
              let l_intEndHour = l_strEndDateInDB.getHours();
              let l_intEndMinutes = l_strEndDateInDB.getMinutes();

              const l_intEventLevel = AMT[j].EventLevel;

              if (
                l_intCurrentYear == l_intYear &&
                l_intCurrentMonth == l_intMonth &&
                l_intCurrentDay == l_intDay
              ) {
                l_intCounter++;
                let l_intHour = m_arrHoursRef.current[i].id;
                l_intHour = parseInt(l_intHour);
                if (l_intStartHour == l_intHour) {
                  if (l_boolAllDay == 1 || l_boolAllDay == true) {
                    l_intStartHour = 0;
                    l_intStartMinutes = 0;
                    l_intHour = 0;
                    l_intEndHour = 23;
                    l_intEndMinutes = 59;
                  }
                  l_boolMatched = true;
                  l_intStartMinutesJson = l_intStartMinutes;
                  if (l_intCurrentDay == l_intEndDay)
                    l_intDurationMinutes =
                      Math.abs(l_intEndHour - l_intStartHour) * 60 +
                      (l_intEndMinutes - l_intStartMinutes);
                  else
                    l_intDurationMinutes =
                      Math.abs(23 - l_intStartHour) * 60 +
                      (60 - l_intStartMinutes);

                  if (AMT[j].Title) l_strText = AMT[j].Title;
                  else if (AMT[j].Action) l_strText = AMT[j].Action;
                  else l_strText = "Some text to display.";
                  if (AMT[j].Tooltip) l_strTooltipText = AMT[j].Tooltip;
                  else l_strTooltipText = "Tooltip data";

                  if (l_intStartHour < 10)
                    l_intStartHour = "0" + l_intStartHour;
                  if (l_intStartMinutes < 10)
                    l_intStartMinutes = "0" + l_intStartMinutes;
                  if (l_intEndHour < 10) l_intEndHour = "0" + l_intEndHour;
                  if (l_intEndMinutes < 10)
                    l_intEndMinutes = "0" + l_intEndMinutes;

                  if (l_boolAllDay) {
                    l_strStartTime =
                      l_intDay + "/" + (l_intMonth + 1) + "/" + l_intYear;
                    l_strEndTime =
                      l_intEndDay +
                      "/" +
                      (l_intEndMonth + 1) +
                      "/" +
                      l_intEndYear;
                  } else {
                    l_strStartTime =
                      l_intDay +
                      "/" +
                      (l_intMonth + 1) +
                      "/" +
                      l_intYear +
                      ", " +
                      l_intStartHour +
                      ":" +
                      l_intStartMinutes;
                    l_strEndTime =
                      l_intEndDay +
                      "/" +
                      (l_intEndMonth + 1) +
                      "/" +
                      l_intEndYear +
                      ", " +
                      l_intEndHour +
                      ":" +
                      l_intEndMinutes;
                  }

                  const l_strHour: any = l_intHour;
                  const l_strID = l_intHour + "hr" + AMT[j].UserID;

                  const l_intIndex = GetIndexExactlyMatchingColumn(
                    l_arrEvents,
                    "id",
                    l_strID
                  );
                  if (l_intIndex > -1) {
                    l_arrEvents[l_intIndex].Count++;
                    l_intCounter = l_arrEvents[l_intIndex].Count;
                  }

                  l_arrEvents[l_intCount] = {
                    id: l_strID,
                    id2: l_strID + "" + l_intCounter,
                    Hour: l_strHour,
                    Text: l_strText,
                    Tooltip: l_strTooltipText,
                    StartMinutes: l_intStartMinutesJson,
                    Duration: l_intDurationMinutes,
                    StartTime: l_strStartTime,
                    EndTime: l_strEndTime,
                    Count: l_intCounter,
                    Color: l_strColor,
                    URL: l_strURL,
                    PersonalLeave: l_boolIsPersonalLeave,
                    EventLevel: l_intEventLevel,
                  };
                  l_arrEventsForJson[l_intCount] = {
                    id: l_strID,
                    id2: l_strID + "" + l_intCounter,
                    StartTime: l_strStartTime,
                    EndTime: l_strEndTime,
                    Text: l_strText,
                    URL: l_strURL,
                  };
                  l_intCount++;
                }
              } else if (
                l_intCurrentYear == l_intEndYear &&
                l_intCurrentMonth == l_intEndMonth &&
                l_intCurrentDay == l_intEndDay
              ) {
                l_intCounter++;
                let l_intHour = m_arrHoursRef.current[i].id;
                l_intHour = parseInt(l_intHour);
                if (l_intEndHour == l_intHour) {
                  if (l_boolAllDay == 1 || l_boolAllDay == true) {
                    l_intEndHour = 23;
                    l_intEndMinutes = 59;
                  }
                  l_boolMatched = true;
                  l_intStartMinutesJson = 0;
                  l_intDurationMinutes =
                    Math.abs(l_intEndHour - 0) * 60 + (l_intEndMinutes - 0);

                  if (AMT[j].Title) l_strText = AMT[j].Title;
                  else if (AMT[j].Action) l_strText = AMT[j].Action;
                  else l_strText = "Some text to display.";
                  if (AMT[j].Tooltip) l_strTooltipText = AMT[j].Tooltip;
                  else l_strTooltipText = "Tooltip data";

                  if (l_intStartHour < 10)
                    l_intStartHour = "0" + l_intStartHour;
                  if (l_intStartMinutes < 10)
                    l_intStartMinutes = "0" + l_intStartMinutes;
                  if (l_intEndHour < 10) l_intEndHour = "0" + l_intEndHour;
                  if (l_intEndMinutes < 10)
                    l_intEndMinutes = "0" + l_intEndMinutes;

                  if (l_boolAllDay) {
                    l_strStartTime =
                      l_intDay + "/" + (l_intMonth + 1) + "/" + l_intYear;
                    l_strEndTime =
                      l_intEndDay +
                      "/" +
                      (l_intEndMonth + 1) +
                      "/" +
                      l_intEndYear;
                  } else {
                    l_strStartTime =
                      l_intDay +
                      "/" +
                      (l_intMonth + 1) +
                      "/" +
                      l_intYear +
                      ", " +
                      l_intStartHour +
                      ":" +
                      l_intStartMinutes;
                    l_strEndTime =
                      l_intEndDay +
                      "/" +
                      (l_intEndMonth + 1) +
                      "/" +
                      l_intEndYear +
                      ", " +
                      l_intEndHour +
                      ":" +
                      l_intEndMinutes;
                  }

                  let l_strHour: any = 0;
                  let l_strID: any = "0hr" + AMT[j].UserID;
                  if (!Show24Hours) {
                    l_strHour = 8;
                    l_strID = "8hr" + AMT[j].UserID;
                  }

                  const l_intIndex = GetIndexExactlyMatchingColumn(
                    l_arrEvents,
                    "id",
                    l_strID
                  );
                  if (l_intIndex > -1) {
                    l_arrEvents[l_intIndex].Count++;
                    l_intCounter = l_arrEvents[l_intIndex].Count;
                  }

                  l_arrEvents[l_intCount] = {
                    id: l_strID,
                    id2: l_strID + "" + l_intCounter,
                    Hour: l_strHour,
                    Text: l_strText,
                    Tooltip: l_strTooltipText,
                    StartMinutes: l_intStartMinutesJson,
                    Duration: l_intDurationMinutes,
                    StartTime: l_strStartTime,
                    EndTime: l_strEndTime,
                    Count: l_intCounter,
                    Color: l_strColor,
                    URL: l_strURL,
                    PersonalLeave: l_boolIsPersonalLeave,
                    EventLevel: l_intEventLevel,
                  };
                  l_arrEventsForJson[l_intCount] = {
                    id: l_strID,
                    id2: l_strID + "" + l_intCounter,
                    StartTime: l_strStartTime,
                    EndTime: l_strEndTime,
                    Text: l_strText,
                    URL: l_strURL,
                  };
                  l_intCount++;
                }
              } else if (
                IsDateBetweenTwoDates(
                  l_oDate,
                  m_oSelectedDate,
                  l_strEndDateInDB
                )
              ) {
                l_intCounter++;
                let l_intHour = m_arrHoursRef.current[i].id;
                l_intHour = parseInt(l_intHour);
                if (l_intHour == 0 || (l_intHour == 8 && !Show24Hours)) {
                  l_boolMatched = true;
                  l_intStartMinutesJson = 0;
                  l_intDurationMinutes =
                    Math.abs(23 - l_intHour) * 60 + (60 - 0);

                  if (AMT[j].Title) l_strText = AMT[j].Title;
                  else if (AMT[j].Action) l_strText = AMT[j].Action;
                  else l_strText = "Some text to display.";
                  if (AMT[j].Tooltip) l_strTooltipText = AMT[j].Tooltip;
                  else l_strTooltipText = "Tooltip data";

                  if (l_intStartHour < 10)
                    l_intStartHour = "0" + l_intStartHour;
                  if (l_intStartMinutes < 10)
                    l_intStartMinutes = "0" + l_intStartMinutes;
                  if (l_intEndHour < 10) l_intEndHour = "0" + l_intEndHour;
                  if (l_intEndMinutes < 10)
                    l_intEndMinutes = "0" + l_intEndMinutes;

                  if (l_boolAllDay) {
                    l_strStartTime =
                      l_intDay + "/" + (l_intMonth + 1) + "/" + l_intYear;
                    l_strEndTime =
                      l_intEndDay +
                      "/" +
                      (l_intEndMonth + 1) +
                      "/" +
                      l_intEndYear;
                  } else {
                    l_strStartTime =
                      l_intDay +
                      "/" +
                      (l_intMonth + 1) +
                      "/" +
                      l_intYear +
                      ", " +
                      l_intStartHour +
                      ":" +
                      l_intStartMinutes;
                    l_strEndTime =
                      l_intEndDay +
                      "/" +
                      (l_intEndMonth + 1) +
                      "/" +
                      l_intEndYear +
                      ", " +
                      l_intEndHour +
                      ":" +
                      l_intEndMinutes;
                  }

                  const l_strHour: any = l_intHour;
                  const l_strID = l_intHour + "hr" + AMT[j].UserID;

                  const l_intIndex = GetIndexExactlyMatchingColumn(
                    l_arrEvents,
                    "id",
                    l_strID
                  );
                  if (l_intIndex > -1) {
                    l_arrEvents[l_intIndex].Count++;
                    l_intCounter = l_arrEvents[l_intIndex].Count;
                  }

                  l_arrEvents[l_intCount] = {
                    id: l_strID,
                    id2: l_strID + "" + l_intCounter,
                    Hour: l_strHour,
                    Text: l_strText,
                    Tooltip: l_strTooltipText,
                    StartMinutes: l_intStartMinutesJson,
                    Duration: l_intDurationMinutes,
                    StartTime: l_strStartTime,
                    EndTime: l_strEndTime,
                    Count: l_intCounter,
                    Color: l_strColor,
                    URL: l_strURL,
                    PersonalLeave: l_boolIsPersonalLeave,
                    EventLevel: l_intEventLevel,
                  };
                  l_arrEventsForJson[l_intCount] = {
                    id: l_strID,
                    id2: l_strID + "" + l_intCounter,
                    StartTime: l_strStartTime,
                    EndTime: l_strEndTime,
                    Text: l_strText,
                    URL: l_strURL,
                  };
                  l_intCount++;
                }
              }
            }
          }
        }
      }
      
      // sort by EventLevel similar to SortJsonData().transform(l_arrEvents, 'EventLevel');
      const sorted = l_arrEvents.sort((a: any, b: any) => {
        if (a.EventLevel == null && b.EventLevel == null) return 0;
        if (a.EventLevel == null) return 1;
        if (b.EventLevel == null) return -1;
        return a.EventLevel - b.EventLevel;
      });

      CalculatedDataFromDatesRef.current = sorted;
      setCalculatedDataFromDates(sorted);

      setMArrEvents(l_arrEventsForJson);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [m_oSelectedDate, m_oToday, m_oCurrentPage, m_arrHours]
  );

  // Called to bind event boxes into DOM (retains original DOM-manipulation approach)
  const BindEventsBoxesFromJson = useCallback(() => {
    const l_arrOverlappingEvents: any[] = [];
    let l_intCounter = 0;

    const tBodyEl = document.getElementById("WeekViewCalendarTBody");
    const decWidth = tBodyEl ? tBodyEl.offsetWidth : 0;

    const tBodyElFirstChild = document.getElementById("WeekViewCalendarTBodyFirstChild");
    const decWidthFirstChild = tBodyElFirstChild ? tBodyElFirstChild.offsetWidth : 0;

    const total = decWidth - decWidthFirstChild;
    setTotalWidth(total);
    if (Show24Hours) {
      const per = total / 24;
      setPerBoxWidth(per);
      PerBoxWidthRef.current = per;
    } else {
      const per = total / 12;
      setPerBoxWidth(per);
      PerBoxWidthRef.current = per;
    }

    for (let i = 0; i < CalculatedDataFromDatesRef.current.length; i++) {
      const item = CalculatedDataFromDatesRef.current[i];
      const l_strElementID = item.id;
      const l_oElement = document.getElementById("tdColumn_" + l_strElementID);

      if (l_oElement != null) {
        if (item.Text != "") {
          const l_strTextToDisplay = item.Text;
          const l_intStartMinutes = item.StartMinutes;
          const l_intDuration = item.Duration;
          const l_strStartTime = item.StartTime;
          const l_strEndTime = item.EndTime;
          const l_intHour = item.Hour;
          const l_strColor = item.Color;
          const l_boolIsPersonalLeave = item.PersonalLeave;
          const l_intEventLevel = item.EventLevel;

          const divRelative = document.createElement("li");
          divRelative.setAttribute(
            "style",
            "position:relative;list-style-type:none;"
          );
          divRelative.setAttribute("class", "CalendarEventColor");
          const divAbsolute = document.createElement("div");
          divRelative.appendChild(divAbsolute);

          if (l_boolIsPersonalLeave) {
            divAbsolute.style.marginTop = "-30px";
          } else {
            divRelative.setAttribute("class", "CalendarEventColor");
          }

          const l_strBottomBorder = GetBottomBorderForJsonEventsBoxes(
            l_intHour,
            l_intDuration,
            l_intStartMinutes
          );
          if (l_strBottomBorder === "0")
            divAbsolute.setAttribute("class", "radiusbord box-radius");
          else divAbsolute.setAttribute("class", "borderradius box-radius");

          if (l_intEventLevel == 1) {
            if (l_boolIsPersonalLeave) {
              divAbsolute.setAttribute(
                "style",
                "padding:2px; font-size:10px; text-align:left; line-height:12px;overflow:hidden; color:#ff6961 !important;"
              );
            } else {
              divAbsolute.setAttribute(
                "style",
                "padding:2px; font-size:10px; text-align:left; line-height:12px;overflow:hidden; color:#000000;"
              );
            }
          } else {
            if (l_boolIsPersonalLeave) {
              divAbsolute.setAttribute(
                "style",
                "padding:2px; font-size:10px; text-align:left; line-height:12px;overflow:hidden; color:#ff6961 !important;z-index:1000;"
              );
            } else {
              divAbsolute.setAttribute(
                "style",
                "padding:2px; font-size:10px; text-align:left; line-height:12px;overflow:hidden; color:#000000;z-index:1000;"
              );
            }
          }

          divAbsolute.style.position = "absolute";
          divAbsolute.style.height = "45px";
          divAbsolute.style.backgroundColor = l_strColor;

          let l_boolExisted = false;
          
            let l_decCalculatedWidth= GetHeightForCellForJsonEvents(
                l_intHour,
                l_intDuration,
                l_intStartMinutes
              );

              if(l_decCalculatedWidth > total)
                l_decCalculatedWidth=total;

          for (let j = 0; j < l_arrOverlappingEvents.length; j++) {
            if (l_strElementID == l_arrOverlappingEvents[j].id) {
              if (l_arrOverlappingEvents[j].Count < 10) {
                divAbsolute.style.marginLeft =
                  l_arrOverlappingEvents[j].Count * 15 +
                  l_arrOverlappingEvents[j].MarginLeft +
                  "px";
                divAbsolute.style.width =
                  l_decCalculatedWidth -
                  l_arrOverlappingEvents[j].Count * 15 +
                  "px";
              }
              l_arrOverlappingEvents[j].Count =
                l_arrOverlappingEvents[j].Count + 1;
              l_boolExisted = true;
            }
          }
          if (!l_boolExisted) {

            divAbsolute.style.marginLeft =
              l_intStartMinutes * (PerBoxWidthRef.current / 60) - 5 + "px";

            divAbsolute.style.width =
              l_decCalculatedWidth + "px";
            l_arrOverlappingEvents[l_intCounter] = {
              id: l_strElementID,
              Count: 1,
              MarginLeft: l_intStartMinutes * (PerBoxWidthRef.current / 60) - 5,
            };
            l_intCounter++;
          }

          divAbsolute.style.marginTop = "-27px";

          if (l_intDuration <= 25)
            divAbsolute.setAttribute("title", l_strTextToDisplay);
          else divAbsolute.setAttribute("title", l_strTextToDisplay);

          if (l_intDuration > 25 && l_intDuration < 45)
            divAbsolute.innerText = l_strTextToDisplay;
          else if (l_intDuration >= 45)
            divAbsolute.innerText = l_strTextToDisplay;

          let l_strElementID2 = item.Tooltip;
          if (
            l_strElementID2 &&
            l_strElementID2 != "" &&
            l_strElementID2.split("JobId: ").length > 1
          ) {
            l_strElementID2 = l_strElementID2.split("JobId: ")[1];
            divAbsolute.addEventListener("click", () =>
              ShowEventsDataFromJson(l_strElementID2, item)
            );
          }
          l_oElement.appendChild(divRelative);
        }
      }
    }

    setIsBinded(true);
  }, [Show24Hours]);

  const GetHeightForCellForJsonEvents = (
    p_intHour: number,
    p_intDuration: number,
    p_intStartMinutes: number
  ) => {
    const l_intPerMinuteWidth = PerBoxWidthRef.current / 60.0;
    const l_intMaxDuration = (Show24Hours ? 23 : 19 - p_intHour) * 60 + (60 - p_intStartMinutes);
    if (p_intDuration > l_intMaxDuration) p_intDuration = l_intMaxDuration;
    const l_intDivHeight = Math.round(p_intDuration * l_intPerMinuteWidth);
    return l_intDivHeight;
  };

  const GetHeightForCell = (
    p_intHour: number,
    p_intDuration: number,
    p_intStartMinutes: number
  ) => {
    const l_intDivHeight = Math.round((p_intDuration / 60) * 40);
    const l_intMaxDuration = (24 - p_intHour) * 60 + (60 - p_intStartMinutes);
    const l_intMaxDivHeight = Math.round((l_intMaxDuration / 60) * 40);
    if (parseInt(String(l_intDivHeight)) > parseInt(String(l_intMaxDivHeight)))
      return l_intMaxDivHeight;
    else return l_intDivHeight;
  };

  const GetBottomBorderForJsonEventsBoxes = (
    p_intHour: number,
    p_intDuration: number,
    p_intStartMinutes: number
  ) => {
    const l_intDivHeight = Math.round((p_intDuration / 60) * 40);
    const l_intMaxDuration = (23 - p_intHour) * 60 + (60 - p_intStartMinutes);
    const l_intMaxDivHeight = Math.round((l_intMaxDuration / 60) * 40);
    if (parseInt(String(l_intDivHeight)) >= parseInt(String(l_intMaxDivHeight)))
      return "0";
    else return "2";
  };

  // Remove previously binded events boxes
  const RemovePreviouslyBindedEventsBoxesFromJson_Called = () => {
    // for (let i = 0; i < CalculatedDataFromDatesRef.current.length; i++) {
    //   const l_strElementID = CalculatedDataFromDatesRef.current[i].id;
    //   const l_oElement = document.getElementById("tdColumn_" + l_strElementID);
    //   if (l_oElement != null) l_oElement.innerText = "";
    // }
    const allColumns = document.querySelectorAll('[id^="tdColumn_"]');
    allColumns.forEach((el) => {
      el.innerHTML = ""; // clear all content
    });
  };

  // Bind public holidays
  const BindPublicHolidays = useCallback(() => {
    const l_arrCalendarData = m_arrDayDates;
    for (let i = 0; i < l_arrCalendarData.length; i++) {
      const l_oCurrentDate = new Date(l_arrCalendarData[i].DateName);
      const l_strDay = m_arrDayNames[i];
      for (let k = 0; k < PublicHolidays.length; k++) {
        const l_strStartDateInDB = PublicHolidays[k].StartDate;
        const l_strEndDateInDB = PublicHolidays[k].EndDate;
        const l_strTitle = PublicHolidays[k].Purpose;
        if (l_strStartDateInDB != "" && l_strEndDateInDB != "") {
          const l_oStartDate = new Date(l_strStartDateInDB);
          const l_oEndDate = new Date(l_strEndDateInDB);
          if (IsDateBetweenTwoDates(l_oStartDate, l_oCurrentDate, l_oEndDate)) {
            for (let j = 0; j < m_arrHoursRef.current.length; j++) {
              const l_oElement = document.getElementById(
                "tdSpan_" + m_arrHoursRef.current[j].id + "" + l_strDay
              );
              if (l_oElement) {
                l_oElement.setAttribute("class", "publicholidays");
                l_oElement.setAttribute("title", l_strTitle);
              } else {
                const span = document.createElement("span");
                span.setAttribute(
                  "id",
                  "tdSpan_" + m_arrHoursRef.current[j].id + "" + l_strDay
                );
                span.setAttribute("class", "publicholidays");
                span.setAttribute("title", l_strTitle);
                const l_oElement2 = document.getElementById(
                  "tdColumn_" + m_arrHoursRef.current[j].id + "" + l_strDay
                );
                if (l_oElement2) l_oElement2.appendChild(span);
              }
            }
            break;
          } else {
            for (let j = 0; j < m_arrHoursRef.current.length; j++) {
              const l_oElement = document.getElementById(
                "tdSpan_" + m_arrHoursRef.current[j].id + "" + l_strDay
              );
              if (l_oElement) {
                l_oElement.setAttribute("class", "");
                l_oElement.setAttribute("title", "");
              }
            }
          }
        }
      }
    }
  }, [PublicHolidays]);

  // ShowEventsDataFromJson
  const ShowEventsDataFromJson = (p_strID: string, p_oEvent?: any) => {
    window.open("quote_detail/" + p_strID, "_blank");
  };

  // BindEvents wrapper to be called when Index set in original Angular; exactly mirrors behavior
  const BindEvents = (p_intIndex: number) => {
    // in Angular they set Index and call RemovePreviouslyBindedEventsBoxesFromJson and BindEventsBoxesFromJson & BindPublicHolidays
    RemovePreviouslyBindedEventsBoxesFromJson_Called();
    BindEventsBoxesFromJson();
    BindPublicHolidays();
    const tBody = document.getElementById("WeekViewCalendarTBody");
    const tW = tBody ? tBody.clientWidth : 0;
    setTableWidth(tW);
  };

  // RebindCalendar (timeout)
  const RebindCalendar = () => {
    setTimeout(() => {
      RemovePreviouslyBindedEventsBoxesFromJson_Called();
      BindEventsBoxesFromJson();
      BindPublicHolidays();
    }, 1500);
  };

  // ---------- Mouse drag handlers ----------
  useEffect(() => {
    const tablescroll = tablescrollRef.current;
    if (!tablescroll) return;
    const onMouseDown = (event: MouseEvent) => {
      isDraggingRef.current = true;
      lastXRef.current = event.clientX;
      lastYRef.current = event.clientY;
      event.preventDefault();
      if (tablescroll) tablescroll.style.cursor = "grabbing";
    };
    const onMouseMove = (event: MouseEvent) => {
      if (isDraggingRef.current && tablescroll) {
        const diffX = event.clientX - lastXRef.current;
        tablescroll.scrollLeft -= diffX;
        lastXRef.current = event.clientX;
        const diffY = event.clientY - lastYRef.current;
        tablescroll.scrollTop -= diffY;
        lastYRef.current = event.clientY;
      }
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      if (tablescroll) tablescroll.style.cursor = "grab";
    };
    tablescroll.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      tablescroll.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // ---------- scroll sync for wrapper1/wrapper2 ----------
  useEffect(() => {
    const w1 = wrapper1Ref.current;
    const w2 = wrapper2Ref.current;
    if (!w1 || !w2) return;
    const onScroll1 = () => {
      if (w2) w2.scrollLeft = w1.scrollLeft;
    };
    const onScroll2 = () => {
      if (w1) w1.scrollLeft = w2.scrollLeft;
    };
    w1.addEventListener("scroll", onScroll1);
    w2.addEventListener("scroll", onScroll2);
    return () => {
      w1.removeEventListener("scroll", onScroll1);
      w2.removeEventListener("scroll", onScroll2);
    };
  }, []);

  // ---------- onResize effect ----------
  useEffect(() => {
    const onResize = () => {
      setAvailableHeight(window.innerHeight);
      const table = document.getElementById("TableWidth");
      if (table) setTableWidth(table.clientWidth);
    };
    window.addEventListener("resize", onResize);
    // run once
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Equivalent ngOnInit -> initial GenerateHours and ChangeWeek(0)
  useEffect(() => {
    GenerateHours();
    ChangeWeek(0);
    // setTableWidth
    const table = document.getElementById("TableWidth");
    if (table) setTableWidth(table.clientWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Equivalent ngOnChanges -> when Helpers / Show24Hours / EventsList / PublicHolidays change
  useEffect(() => {
    setIsBinded(false);
    // Build users from Helpers (original code just used this.Helpers)
    setUsers(Helpers || []);
    usersRef.current = Helpers || [];

    GenerateHours();
    ChangeWeek(0);

    // sync TableWidth
    const t = document.getElementById("TableWidth");
    if (t) setTableWidth(t.clientWidth);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Helpers, Show24Hours, EventsList, PublicHolidays]);

  // Whenever CalculatedDataFromDates changed, bind events
  useEffect(() => {
    CalculatedDataFromDatesRef.current = CalculatedDataFromDates;
    // remove old nodes first
    RemovePreviouslyBindedEventsBoxesFromJson_Called();
    if (CalculatedDataFromDates.length > 0) {
      BindEventsBoxesFromJson();
      BindPublicHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CalculatedDataFromDates, EventsList, CalculatedDataFromDates,m_arrHoursRef.current]);

  // Whenever m_arrDayDates or AMTJsonDataForEventsState change, regenerate events
  useEffect(() => {
    // Re-check events

    CheckDatesForEventFromJson(EventsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m_arrDayDates, AMTJsonDataForEventsState, EventsList,m_arrHoursRef.current]);

  // ---------- Template handlers ----------
  const handleDayClicked = (ev: any) => {
    if (dayClicked) dayClicked(ev);
  };

  // ---------- Render ----------
  return (
    <>
      {/* mimic (window:resize) by hooking into resize effect */}

      {/* <div
        ref={wrapper1Ref}
        className="mn-scroll-main mn-calendar ib-display stw100 wrapper1"
        style={{ height: 20, overflowY: "hidden", whiteSpace: "nowrap" }}
      >
        <div
          style={{ display: "inline-block", height: 20, width: TableWidth }}
        />
      </div> */}

      <div ref={tablescrollRef} className="flow-auto calendar-table">
        <table
          className={`reuse-table ${
            BankHolidayText != "" ? "bank-holiday" : ""
          }`}
          id="TableWidth"
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead>
            <tr>
              <th>Time</th>
              {m_arrHours.map((Row) => (
                <th key={Row.id}>
                  <span>{Row.Hour}</span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody id="WeekViewCalendarTBody">
            {users.map((user: any, rowNumber: number) => (
              <tr key={user.ID ?? rowNumber}>
                <td id="WeekViewCalendarTBodyFirstChild">
                  <b>{user.Name}</b>
                </td>

                {m_arrHours.map((Row) => {
                  const tdId = `tdColumn_${Row.id}hr${user.ID}`;
                  return <td key={tdId} id={tdId} />;
                })}

                {/* the 'last' trick in Angular: they call BindEvents(last) on last row (hidden) */}
                {rowNumber === users.length - 1 ? (
                  <td style={{ display: "none" }}>
                    {/* placeholder: call BindEvents when needed */}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* TODO: Replace ShowModal with your React modal component */}
      {/* <ShowModal ref={...} /> */}
    </>
  );
};

export default WeekView;
